/**
 * PartialReviewModal — lists every Partial + In-Progress task so the user
 * can scan, open, or recover them. Wired from FocusStripWidget's "X partial"
 * pill (Daily Dashboard V2).
 */
import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertTriangle, Clock3, Calendar, ExternalLink } from 'lucide-react';

const STATUS_TONE = {
  'In Progress': { bg: 'rgba(37, 99, 235, .12)', fg: '#1d4ed8', label: 'In Progress' },
  'Partial':     { bg: 'rgba(217, 119,  6, .14)', fg: '#b45309', label: 'Partial' },
  'Partially Completed': { bg: 'rgba(217, 119, 6, .14)', fg: '#b45309', label: 'Partial' },
  'Blocked':     { bg: 'rgba(220,  38, 38, .12)', fg: '#b91c1c', label: 'Blocked' },
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || { bg: 'rgba(100,116,139,.14)', fg: '#475569', label: status || 'Unknown' };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold"
      style={{ background: tone.bg, color: tone.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.fg }} />
      {tone.label}
    </span>
  );
}

function TaskRow({ task, onOpen }) {
  const date = task.task_local_date || task.task_delivery_date;
  const start = task.task_local_start_time || task.task_start_time;
  const end = task.task_local_end_time || task.task_end_time;
  return (
    <button
      type="button"
      onClick={() => onOpen?.(task)}
      className="w-full text-left flex items-start gap-3 p-3 rounded-lg border orka-border-subtle orka-surface-raised hover:orka-surface-subtle orka-transition"
      data-testid={`partial-modal-task-${task.task_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <StatusPill status={task.task_status} />
          {task.task_estimated_effort && (
            <span className="text-[10px] orka-text-tertiary font-bold">E{task.task_estimated_effort}</span>
          )}
        </div>
        <div className="text-sm font-semibold orka-text-primary truncate">
          {task.task_title || task.title || 'Untitled task'}
        </div>
        <div className="flex items-center gap-3 text-[11px] orka-text-tertiary mt-1">
          {date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>}
          {start && end && <span className="inline-flex items-center gap-1"><Clock3 className="w-3 h-3" />{start}–{end}</span>}
          {task.work_area_name && <span>· {task.work_area_name}</span>}
        </div>
      </div>
      <ExternalLink className="w-4 h-4 orka-text-tertiary flex-none mt-1" />
    </button>
  );
}

export default function PartialReviewModal({ open, onOpenChange, partialTasks = [], plannedTasks = [], onOpenTask }) {
  const items = useMemo(() => {
    const pool = new Map();
    (partialTasks || []).forEach(t => t && pool.set(t.task_id, t));
    (plannedTasks || []).forEach(t => {
      if (t && (t.task_status === 'In Progress' || t.task_status === 'Blocked')) {
        pool.set(t.task_id, t);
      }
    });
    return [...pool.values()];
  }, [partialTasks, plannedTasks]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="partial-review-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Partial &amp; In-Progress Tasks
            <span className="ml-auto text-sm font-normal orka-text-tertiary">{items.length} total</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {items.length === 0 ? (
            <div className="text-center py-12 orka-text-tertiary text-sm">
              No partial or in-progress tasks. You&rsquo;re fully landed.
            </div>
          ) : (
            items.map(t => (
              <TaskRow
                key={t.task_id}
                task={t}
                onOpen={(task) => { onOpenTask?.(task); onOpenChange?.(false); }}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
